import { findLesson } from "./_helpers";
import { LessonShell } from "@/components/LessonShell";
import { Insight } from "@/components/Insight";
import {
  Tip, KeyIdea, Pull, Steps, Step, NextUp,
  KeyHighlights, Highlight,
} from "@/components/Callouts";

export default function PianoFoundation28() {
  const { lesson, course, accent } = findLesson("piano-foundation", "28");
  return (
    <LessonShell
      course={course}
      courseSlug="piano-foundation"
      lessonOrder={lesson.order}
      title={lesson.title}
      description={lesson.desc}
      videoUrl={lesson.video}
      thumbUrl={lesson.thumb}
      accent={accent}
    >
      <KeyHighlights>
        <Highlight>The left hand is a <strong>mirror image</strong> of the right hand. The descending fingering of RH is the ascending fingering of LH — same finger order, opposite direction.</Highlight>
        <Highlight>For most white-note scales: <strong>5 - 4 - 3 - 2 - 1 then cross to 3</strong>. Pinky starts; ring crosses over the thumb halfway through.</Highlight>
        <Highlight>F major is <strong>not</strong> an exception in the left hand — there's no thumb-on-black-key issue going downward. Standard 5+3 fingering works.</Highlight>
        <Highlight>B major <em>is</em> an exception — start with the <strong>ring finger</strong>, not the pinky, so the thumb avoids landing on black keys.</Highlight>
        <Highlight>For black-note scales, most start with the <strong>middle finger</strong> (D♭, E♭, A♭, B♭). F♯ major starts with the <strong>ring finger</strong>. The same crossing rule still applies.</Highlight>
      </KeyHighlights>

      <Insight eyebrow="The mirror principle" title="Left hand = right hand backwards" accent={accent.hex}>
        <Pull>
          The first thing I will say to keep the left hand a bit easy on the mind is
          it's the same as the right hand, except in reverse. It's like a mirror image.
        </Pull>

        <p>
          Once you've internalised right-hand scales, you've already done much of the work for
          the left hand. The fingerings flip vertically. Your left hand thumb sits on the
          octave; your pinky sits on the root. From there, ascending the left hand looks
          identical to descending the right hand.
        </p>

        <KeyIdea>
          You can practise the same scale in both hands at once — playing in <strong>contrary
          motion</strong>. Right hand ascends; left hand descends. Both hands meet at the
          octave. Same fingering, opposite directions. It's surprisingly effective.
        </KeyIdea>
      </Insight>

      <Insight eyebrow="The basic shape" title="5 + 3 ascending — pinky starts, ring crosses" accent={accent.hex}>
        <p>
          For most white-note scales in the left hand, the rule is the mirror of right-hand
          3 + 5: <strong>play 5 (pinky) ... then play 3 again at the cross</strong>. The
          fingering ascending the LH is:
        </p>

        <p className="font-mono text-[14px] text-amber-300">
          5 - 4 - 3 - 2 - 1 — ring crosses over thumb — 3 - 2 - 1
        </p>

        <Tip>
          A major in the LH: pinky on A, ring on B, middle on C♯, index on D, thumb on E,
          then cross the ring over the thumb to play F♯, middle on G♯, thumb on A. Same
          motion the right hand uses to descend.
        </Tip>
      </Insight>

      <Insight eyebrow="The exceptions" title="F major is easy · B major needs to start on the ring" accent={accent.hex}>
        <ul className="space-y-2 text-[14px]">
          <li>
            <strong>F major (LH)</strong> — easier than the right hand! No thumb-on-black-key
            issue. Standard 5 + 3 fingering: pinky on F, ring on G, middle on A, index on B♭,
            thumb on C, then the middle crosses over the thumb to play D, index on E, thumb on F.
          </li>
          <li>
            <strong>B major (LH)</strong> — exception. If you start with the pinky, your
            thumb eventually lands on a black key (no good grip). Start with the
            <strong> ring finger</strong> instead, so the thumb falls on a white key. Then
            cross the ring (not the middle) over the thumb.
          </li>
        </ul>

        <Pull>
          Start the B major scale with your ring finger. I repeat, start B major scale
          in the left hand with the ring finger and do another ring cross. This will
          work out quite well, I think.
        </Pull>
      </Insight>

      <Insight eyebrow="Black-note scales" title="Most start on middle · F♯ starts on ring" accent={accent.hex}>
        <p>
          The black-note scales follow a pattern. Most of them start on the middle finger so
          the thumb doesn't end up on a black key:
        </p>

        <ul className="space-y-1 text-[14px]">
          <li><strong>D♭ major (LH)</strong> — start with middle. Cross the ring (not middle), then thumb-index-middle.</li>
          <li><strong>E♭ major (LH)</strong> — start with middle. Cross the ring as usual.</li>
          <li><strong>A♭ major (LH)</strong> — start with middle. Cross the ring.</li>
          <li><strong>B♭ major (LH)</strong> — same idea. Middle, then ring across.</li>
          <li>
            <strong>F♯ major (LH)</strong> — exception. The third note is black, so middle-start
            puts the thumb on a black key. Solution: start with the <strong>ring finger</strong>{" "}
            and cross the middle (not the ring).
          </li>
        </ul>

        <KeyIdea>
          Same rule as Lesson 26 — never let the thumb land on a black key when there's a
          better option. The crossing fingerings adapt to keep the thumb on white keys
          whenever possible.
        </KeyIdea>
      </Insight>

      <Insight eyebrow="Both hands together" title="Parallel and contrary motion" accent={accent.hex}>
        <p>
          Once each scale is solid in both hands separately, put them together. Two ways:
        </p>

        <ul className="space-y-1 text-[14px]">
          <li>
            <strong>Parallel motion</strong> — both hands in the same direction. Both
            ascending, then both descending. The traditional way.
          </li>
          <li>
            <strong>Contrary motion</strong> — RH ascending while LH descending (or vice
            versa). Both hands meet at the octave. Same fingering across both hands.
          </li>
        </ul>

        <Pull>
          Maybe contrary motion will be easier than the other motion, you know. So let's do
          that.
        </Pull>
      </Insight>

      <Insight eyebrow="Why bother" title="Where left-hand scales actually show up" accent={accent.hex}>
        <p>
          You won't play left-hand scales in pop music every day. But they appear constantly
          in:
        </p>

        <ul className="space-y-1 text-[14px]">
          <li><strong>Bass lines</strong> — walking bass under chord progressions.</li>
          <li><strong>Classical music</strong> — left-hand passages that mirror or counterpoint the right hand.</li>
          <li><strong>Hand independence drills</strong> — playing different things in each hand at once.</li>
        </ul>

        <Pull>
          It's not so common to play scales in the left hand but you may need it from
          time to time, especially when learning bass lines.
        </Pull>
      </Insight>

      <Insight eyebrow="Practice path" title="Mirror what you already know" accent={accent.hex}>
        <Steps>
          <Step>Pick a scale you know well in the right hand (start with C major or A major).</Step>
          <Step>Play that scale in the LH alone, ascending and descending, slowly. Use 5 + 3 fingering.</Step>
          <Step>Now play in <strong>contrary motion</strong> — RH ascends, LH descends. Both meet at the octave.</Step>
          <Step>Then play in <strong>parallel motion</strong> — both hands ascend together. This feels harder but is what most music requires.</Step>
          <Step>Repeat for the other six white-note majors. Then the five black-note majors. Then drag the same fingering through the minors.</Step>
        </Steps>

        <Tip>
          The crossing rules from Lessons 25-27 transfer to the left hand without
          modification. White-note scales: 5 + 3. Thumb-on-black-key exceptions: shift the
          start finger one step. Black-note scales: cross when colour changes. The whole
          system is one set of rules — applied left or right, same logic.
        </Tip>
      </Insight>

      <NextUp
        to="/lesson/piano-foundation-29"
        label="Lesson 29 · How to Remember Piano Chord Shapes (Major & Minor Triads)"
        why="With scales done in both hands, it's time to come back to chords — and learn the visual shape system that helps you find any chord on the keyboard in seconds."
      />
    </LessonShell>
  );
}
